import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "./contexts/AuthContext";
import { SiteSettingsProvider } from "./contexts/SiteSettingsContext";
import { LanguageProvider } from "./contexts/LanguageContext";
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import SignupPage from "./pages/SignupPage";
import PendingPage from "./pages/PendingPage";
import PlatformDetailPage from "./pages/PlatformDetailPage";
import BlogIndexPage from "./pages/BlogIndexPage";
import BlogPostPage from "./pages/BlogPostPage";
import CalculatorPage from "./pages/CalculatorPage";
import ReferralPage from "./pages/ReferralPage";
import TermsPage from "./pages/TermsPage";
import PrivacyPage from "./pages/PrivacyPage";
import RefundPage from "./pages/RefundPage";
import ContactPage from "./pages/ContactPage";
import SolutionLandingPage from "./pages/SolutionLandingPage";
import SolutionsIndexPage from "./pages/SolutionsIndexPage";
import DesignPreviewPage from "./pages/DesignPreviewPage";
import DesignHomepagePreviewPage from "./pages/DesignHomepagePreviewPage";
import ProtectedRoute from "./components/ProtectedRoute";
import PanelLayout from "./components/PanelLayout";
import AdminLayout from "./components/AdminLayout";
import PanelHome from "./pages/panel/PanelHome";
import PanelWallet from "./pages/panel/PanelWallet";
import PanelAdAccounts from "./pages/panel/PanelAdAccounts";
import PanelAffiliate from "./pages/panel/PanelAffiliate";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminApplications from "./pages/admin/AdminApplications";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminUserDetail from "./pages/admin/AdminUserDetail";
import AdminAdAccounts from "./pages/admin/AdminAdAccounts";
import AdminTransactions from "./pages/admin/AdminTransactions";
import AdminWallets from "./pages/admin/AdminWallets";
import AdminSweep from "./pages/admin/AdminSweep";
import NotFoundPage from "./pages/NotFoundPage";
import LocaleLayout from "./components/LocaleLayout";
import AdminSiteSettings from "./pages/admin/AdminSiteSettings";
import AdminAdsTrackingCenter from "./pages/admin/AdminAdsTrackingCenter";
import AdminEventLogs from "./pages/admin/AdminEventLogs";
import AdminDeposits from "./pages/admin/AdminDeposits";
import AdminBlog from "./pages/admin/AdminBlog";
import ScrollToTop from "./components/ScrollToTop";
import LocaleRedirect from "./components/LocaleRedirect";
import MetaPixelTracker from "./components/MetaPixelTracker";
import GA4Tracker from "./components/GA4Tracker";
import "./App.css";

function App() {
    return (
        <div className="App dark min-h-screen bg-background text-foreground">
            <LanguageProvider>
                <SiteSettingsProvider>
                    <AuthProvider>
                        <BrowserRouter>
                            <ScrollToTop />
                            <LocaleRedirect />
                            <MetaPixelTracker />
                            <GA4Tracker />
                            <Routes>
                            {/* Public */}
                            <Route path="/" element={<LandingPage />} />
                            <Route path="/login" element={<LoginPage />} />
                            <Route path="/signup" element={<SignupPage />} />
                            <Route path="/pending" element={<PendingPage />} />
                            <Route path="/platforms/:slug" element={<PlatformDetailPage />} />
                            <Route path="/blog" element={<BlogIndexPage />} />
                            <Route path="/blog/:slug" element={<BlogPostPage />} />
                            <Route path="/calculator" element={<CalculatorPage />} />
                            <Route path="/referral" element={<ReferralPage />} />
                            <Route path="/terms" element={<TermsPage />} />
                            <Route path="/privacy" element={<PrivacyPage />} />
                            <Route path="/refund" element={<RefundPage />} />
                            <Route path="/contact" element={<ContactPage />} />
                            <Route path="/design-preview" element={<DesignPreviewPage />} />
                            <Route path="/design-preview/home/:style" element={<DesignHomepagePreviewPage />} />
                            <Route path="/solutions" element={<SolutionsIndexPage />} />
                            <Route path="/solutions/:slug" element={<SolutionLandingPage />} />

                            {/* Localized public routes: /tr, /es, /pt, /ru */}
                            <Route path="/:lang" element={<LocaleLayout />}>
                                <Route index element={<LandingPage />} />
                                <Route path="login" element={<LoginPage />} />
                                <Route path="signup" element={<SignupPage />} />
                                <Route path="platforms/:slug" element={<PlatformDetailPage />} />
                                <Route path="blog" element={<BlogIndexPage />} />
                                <Route path="blog/:slug" element={<BlogPostPage />} />
                                <Route path="calculator" element={<CalculatorPage />} />
                                <Route path="referral" element={<ReferralPage />} />
                                <Route path="terms" element={<TermsPage />} />
                                <Route path="privacy" element={<PrivacyPage />} />
                                <Route path="refund" element={<RefundPage />} />
                                <Route path="contact" element={<ContactPage />} />
                                <Route path="solutions" element={<SolutionsIndexPage />} />
                                <Route path="solutions/:slug" element={<SolutionLandingPage />} />
                                <Route path="*" element={<NotFoundPage />} />
                            </Route>

                            {/* User panel */}
                            <Route
                                path="/panel"
                                element={
                                    <ProtectedRoute>
                                        <PanelLayout />
                                    </ProtectedRoute>
                                }
                            >
                                <Route index element={<PanelHome />} />
                                <Route path="wallet" element={<PanelWallet />} />
                                <Route path="ad-accounts" element={<PanelAdAccounts />} />
                                <Route path="affiliate" element={<PanelAffiliate />} />
                            </Route>

                            {/* Admin */}
                            <Route
                                path="/admin"
                                element={
                                    <ProtectedRoute adminOnly>
                                        <AdminLayout />
                                    </ProtectedRoute>
                                }
                            >
                                <Route index element={<AdminDashboard />} />
                                <Route path="applications" element={<AdminApplications />} />
                                <Route path="users" element={<AdminUsers />} />
                                <Route path="users/:id" element={<AdminUserDetail />} />
                                <Route path="ad-accounts" element={<AdminAdAccounts />} />
                                <Route path="transactions" element={<AdminTransactions />} />
                                <Route path="deposits" element={<AdminDeposits />} />
                                <Route path="blog" element={<AdminBlog />} />
                                <Route path="wallets" element={<AdminWallets />} />
                                <Route path="sweep" element={<AdminSweep />} />
                                <Route path="site-settings" element={<AdminSiteSettings />} />
                                    <Route path="ads-tracking" element={<AdminAdsTrackingCenter />} />
                                    <Route path="event-logs" element={<AdminEventLogs />} />
                            </Route>

                            <Route path="*" element={<NotFoundPage />} />
                            </Routes>
                        </BrowserRouter>
                        <Toaster
                            theme="dark"
                            position="top-right"
                            toastOptions={{
                                style: {
                                    background: "#111113",
                                    border: "1px solid rgba(255,255,255,0.08)",
                                    color: "#fff",
                                },
                            }}
                        />
                    </AuthProvider>
                </SiteSettingsProvider>
            </LanguageProvider>
        </div>
    );
}

export default App;
